import React from 'react';

import { Caption, Heading } from '@depop/web-thrift-ui/Typography';
import { LikeIcon } from '@depop/web-ui-kit/Icons/LikeIcon';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { isSold } from '@/modules/product/helpers/isSold';
import {
  type LegacyProductStatus,
  type ProductStatus,
} from '@/modules/product/types';

type Props = {
  status?: ProductStatus | LegacyProductStatus;
  likeCount?: number;
  src?: string;
  className?: string;
  isInteractive?: boolean;
  altText?: string;
  displayBoostedOverlay?: boolean;
  hasProductInsights?: boolean;
};

/**
 * @param src
 * @param {number=} likeCount - pass undefined to not render likes info at all
 * @param status
 * @param className
 * @param isInteractive
 * @param altText
 * @param displayBoostedOverlay
 * @param hasProductInsights
 */
function SellingItemImage({
  src,
  likeCount,
  status,
  className,
  isInteractive = false,
  altText = '',
  displayBoostedOverlay = false,
  hasProductInsights,
}: Props) {
  const commonT = useTranslations('common');
  const itemSold = status && isSold(status);

  const shouldDisplayLikes =
    !itemSold && !hasProductInsights && typeof likeCount === 'number';

  return (
    <div
      className={clsx(styles.imageWrapper, className, {
        [styles.interactiveImageWrapper]: isInteractive,
      })}
    >
      {shouldDisplayLikes && (
        <div className={styles.likesWrapper}>
          <LikeIcon size={18} />
          <Caption variant="lg-regular" className={styles.likesNumber}>
            {likeCount}
          </Caption>
        </div>
      )}
      <img src={src} alt={altText} className={styles.image} />
      {itemSold && (
        <div className={styles.imageOverlay}>
          <Heading variant="sm" as="p" className={styles.soldText}>
            {commonT('SoldSingular')}
          </Heading>
        </div>
      )}
      {!itemSold && displayBoostedOverlay && (
        <div className={styles.imageOverlay}>
          <Heading variant="sm" as="p" className={styles.boostedText}>
            {commonT('BoostedListings.Boosted')}
          </Heading>
        </div>
      )}
    </div>
  );
}

export { SellingItemImage };
